/**
 * Scrolling to a message, shared by the pinned list and reply quotes.
 *
 * Both used to call `getElementById(...)?.scrollIntoView()` inline, which fails
 * silently in the one case that matters: the target is usually an *older*
 * message, and only the last few pages are mounted. `useJumpToMessage` below
 * pages backwards until the message exists, then scrolls to it.
 */
import { useCallback, useRef, useState } from 'react';

/** Keep in sync with the `team-chat-jump-flash` animation in index.css. */
const FLASH_MS = 1600;

/**
 * Scroll to a message if it is currently mounted. Returns false when it isn't,
 * so callers can decide whether to load more pages.
 */
export function scrollToMessage(messageId: string): boolean {
  const el = document.getElementById(`team-chat-message-${messageId}`);
  if (!el) return false;
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  // Landing mid-history is disorienting without a marker: flash the row so the
  // eye can find what it was sent to.
  el.classList.remove('team-chat-jump-flash');
  // Force a reflow so re-adding the class restarts the animation when the same
  // message is jumped to twice in a row.
  void el.offsetWidth;
  el.classList.add('team-chat-jump-flash');
  window.setTimeout(() => el.classList.remove('team-chat-jump-flash'), FLASH_MS);
  return true;
}

/**
 * Bound on how far back a jump will page. At 50 messages per page this reaches
 * ~500 messages; beyond that the wait is worse than the miss, and the caller
 * surfaces "couldn't reach it".
 */
const MAX_PAGES = 10;

export function useJumpToMessage(fetchNextPage: () => Promise<{ hasNextPage: boolean }>): {
  jumpTo: (messageId: string) => void;
  jumping: boolean;
  missed: boolean;
} {
  const [jumping, setJumping] = useState(false);
  const [missed, setMissed] = useState(false);
  // Guards against a second jump racing the first through the same pages.
  const busyRef = useRef(false);

  const jumpTo = useCallback(
    (messageId: string) => {
      setMissed(false);
      if (scrollToMessage(messageId) || busyRef.current) return;

      busyRef.current = true;
      setJumping(true);
      void (async () => {
        try {
          for (let i = 0; i < MAX_PAGES; i += 1) {
            const { hasNextPage } = await fetchNextPage();
            // Let the new page commit and paint before looking for the node.
            await new Promise((resolve) => requestAnimationFrame(resolve));
            if (scrollToMessage(messageId)) return;
            if (!hasNextPage) break;
          }
          setMissed(true);
        } finally {
          busyRef.current = false;
          setJumping(false);
        }
      })();
    },
    [fetchNextPage],
  );

  return { jumpTo, jumping, missed };
}
